export interface PreviewColumn {
  /** Key of the value inside each row object. */
  current: string;
  /** Label shown in the table header. */
  modified: string;
}

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  column: string;
  direction: SortDirection;
}
/** column key -> values that are allowed to remain. */
export type FiltersState = Record<string, string[]>;

const NUMBER_PATTERN = /^-?[\d,]*\.?\d+%?$/;

const toText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const toNumber = (value: string): number => Number(value.replace(/[%,]/g, ''));

export const isNumericColumn = (rows: any[], column: string): boolean => {
  let seen = 0;
  for (let index = 0; index < rows.length && seen < 50; index += 1) {
    const value = toText(rows[index]?.[column]).trim();
    if (value !== '') {
      if (!NUMBER_PATTERN.test(value)) return false;
      seen += 1;
    }
  }
  return seen > 0;
};

export const getDistinctValues = (rows: any[], column: string, limit = 300): string[] => {
  const values = new Set<string>();
  for (let index = 0; index < rows.length && values.size < limit; index += 1) {
    const value = toText(rows[index]?.[column]);
    if (value !== '') values.add(value);
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

const compareValues = (a: string, b: string): number => {
  if (a === '' && b === '') return 0;
  if (a === '') return 1;
  if (b === '') return -1;
  if (NUMBER_PATTERN.test(a.trim()) && NUMBER_PATTERN.test(b.trim())) {
    return toNumber(a.trim()) - toNumber(b.trim());
  }
  return a.localeCompare(b, undefined, { numeric: true });
};

export const applyPreviewTransforms = (
  rows: any[],
  options: {
    search: string;
    filters: FiltersState;
    sort: SortState | null;
    searchColumns: string[];
  },
): any[] => {
  const term = options.search.trim().toLowerCase();
  const activeFilters = Object.entries(options.filters).filter(([, values]) => values.length > 0);

  let result = rows;
  if (activeFilters.length > 0) {
    result = result.filter((row) =>
      activeFilters.every(([column, values]) => values.includes(toText(row?.[column]))),
    );
  }
  if (term) {
    result = result.filter((row) =>
      options.searchColumns.some((column) => toText(row?.[column]).toLowerCase().includes(term)),
    );
  }
  if (options.sort) {
    const { column, direction } = options.sort;
    const factor = direction === 'asc' ? 1 : -1;
    result = [...result].sort(
      (a, b) => factor * compareValues(toText(a?.[column]), toText(b?.[column])),
    );
  }
  return result;
};

const csvEscape = (value: string): string =>
  /[\n\r",]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export const buildCsv = (columns: PreviewColumn[], rows: any[]): string => {
  const header = columns.map((column) => csvEscape(column.modified)).join(',');
  const lines = rows.map((row) =>
    columns.map((column) => csvEscape(toText(row?.[column.current]))).join(','),
  );
  return [header, ...lines].join('\n');
};

export const saveCsv = (fileName: string, content: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** 1 … 4 5 6 … 20 style window for the pagination control. */
export const getPageWindow = (
  current: number,
  total: number,
): (number | 'gap-start' | 'gap-end')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const pages: (number | 'gap-start' | 'gap-end')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('gap-start');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 1) pages.push('gap-end');
  pages.push(total);
  return pages;
};
